import { useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminReviews, useReviewStats, ReviewStatus, AdminReview } from "@/hooks/useAdminReviews";
import { useReviewModeration } from "@/hooks/useReviewModeration";
import ReviewDetailsDialog from "@/components/admin/ReviewDetailsDialog";
import ReviewStatusBadge from "@/components/admin/ReviewStatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Star,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  MessageSquare,
  Clock,
  TrendingUp,
  BarChart,
} from "lucide-react";
import { format } from "date-fns";

const AdminReviews = () => {
  const [selectedStatus, setSelectedStatus] = useState<ReviewStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedReviews, setSelectedReviews] = useState<Set<string>>(new Set());
  const [detailsReview, setDetailsReview] = useState<AdminReview | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const { data: reviews = [], isLoading } = useAdminReviews({
    status: selectedStatus,
    searchQuery,
  });

  const { data: stats } = useReviewStats();
  const { approveReview, rejectReview, bulkApprove, bulkReject, isLoading: isModerating } = useReviewModeration();

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedReviews(new Set(reviews.map(r => r.id)));
    } else {
      setSelectedReviews(new Set());
    }
  };

  const handleSelectReview = (reviewId: string, checked: boolean) => {
    const newSelected = new Set(selectedReviews);
    if (checked) {
      newSelected.add(reviewId);
    } else {
      newSelected.delete(reviewId);
    }
    setSelectedReviews(newSelected);
  };

  const handleBulkApprove = () => {
    if (selectedReviews.size > 0) {
      bulkApprove(Array.from(selectedReviews));
      setSelectedReviews(new Set());
    }
  };

  const handleBulkReject = () => {
    if (selectedReviews.size > 0) {
      bulkReject(Array.from(selectedReviews));
      setSelectedReviews(new Set());
    }
  };

  const handleViewDetails = (review: AdminReview) => {
    setDetailsReview(review);
    setDetailsOpen(true);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Review Management</h1>
          <p className="text-muted-foreground">
            Moderate and manage customer product reviews
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Reviews</CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total_reviews}</div>
                <p className="text-xs text-muted-foreground">
                  Across all products
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending</CardTitle>
                <Clock className="h-4 w-4 text-yellow-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.pending_count}</div>
                <p className="text-xs text-muted-foreground">
                  Awaiting moderation
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Approval Rate</CardTitle>
                <TrendingUp className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.approval_rate}%</div>
                <p className="text-xs text-muted-foreground">
                  {stats.approved_count} approved
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Rating</CardTitle>
                <BarChart className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold flex items-center gap-1">
                  {stats.average_rating}
                  <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                </div>
                <p className="text-xs text-muted-foreground">
                  Overall average
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters and Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by product, reviewer, or title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          {selectedReviews.size > 0 && (
            <div className="flex gap-2">
              <Button
                variant="default"
                onClick={handleBulkApprove}
                disabled={isModerating}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve ({selectedReviews.size})
              </Button>
              <Button
                variant="outline"
                onClick={handleBulkReject}
                disabled={isModerating}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject ({selectedReviews.size})
              </Button>
            </div>
          )}
        </div>

        {/* Reviews Table with Tabs */}
        <Tabs value={selectedStatus} onValueChange={(v) => setSelectedStatus(v as ReviewStatus | 'all')}>
          <TabsList>
            <TabsTrigger value="all">
              All ({stats?.total_reviews || 0})
            </TabsTrigger>
            <TabsTrigger value="pending">
              Pending ({stats?.pending_count || 0})
            </TabsTrigger>
            <TabsTrigger value="approved">
              Approved ({stats?.approved_count || 0})
            </TabsTrigger>
            <TabsTrigger value="rejected">
              Rejected ({stats?.rejected_count || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={selectedStatus} className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Reviews</CardTitle>
                <CardDescription>
                  {selectedStatus === 'all' 
                    ? 'All customer reviews across products'
                    : `${selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)} reviews`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Loading reviews...
                  </div>
                ) : reviews.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No reviews found
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedReviews.size === reviews.length && reviews.length > 0}
                            onCheckedChange={handleSelectAll}
                          />
                        </TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead>Reviewer</TableHead>
                        <TableHead>Rating</TableHead>
                        <TableHead>Review</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reviews.map((review) => (
                        <TableRow key={review.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedReviews.has(review.id)}
                              onCheckedChange={(checked) => 
                                handleSelectReview(review.id, checked as boolean)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {review.product_image && (
                                <img
                                  src={review.product_image}
                                  alt={review.product_name || "Product"}
                                  className="h-10 w-10 rounded-md object-cover"
                                />
                              )}
                              <span className="font-medium text-sm max-w-[200px] truncate">
                                {review.product_name || "Unknown"}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={review.reviewer_avatar || undefined} />
                                <AvatarFallback>
                                  {review.reviewer_name?.[0]?.toUpperCase() || 'A'}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="text-sm font-medium">
                                  {review.reviewer_name || review.reviewer_email || 'Anonymous User'}
                                </p>
                                {review.verified_purchase && (
                                  <Badge variant="secondary" className="text-xs">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Verified
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-4 w-4 ${
                                    i < review.rating
                                      ? "fill-yellow-400 text-yellow-400"
                                      : "fill-muted text-muted-foreground"
                                  }`}
                                />
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="max-w-[300px]">
                              {review.title && (
                                <p className="font-medium text-sm truncate">{review.title}</p>
                              )}
                              <p className="text-xs text-muted-foreground truncate">
                                {review.content || "No content"}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <ReviewStatusBadge status={review.status} />
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {format(new Date(review.created_at), "MMM d, yyyy")}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewDetails(review)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {review.status !== 'approved' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => approveReview(review.id)}
                                  disabled={isModerating}
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                              )}
                              {review.status !== 'rejected' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => rejectReview(review.id)}
                                  disabled={isModerating}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Details Dialog */}
      <ReviewDetailsDialog
        review={detailsReview}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
      />
    </AdminLayout>
  );
};

export default AdminReviews;
